/* eslint-disable react/jsx-props-no-spreading */
import { useInfiniteQuery, useQueries, useQuery } from '@tanstack/react-query';
import { Grommet, Select, SelectExtendedProps, SelectMultiple, SelectMultipleExtendedProps, ThemeType } from 'grommet';
import { debounce } from 'lodash-es';
import { useMemo, useState } from 'preact/hooks';

import grommetTheme from '@/grommetTheme';
import { getInfiniteAdminPlaylistsQueryOptions, getPlaylistQueryOption } from '@/queries/playlists';
import { Playlist } from '@/queries/playlists/types';

/** Rows passed to Grommet Select — only what `labelKey` / `valueKey` need. */
interface PlaylistSelectOption {
  _id: string;
  title: string;
}

function optionPlaceholder(id: string): PlaylistSelectOption {
  return { _id: id, title: id };
}

function playlistToSelectOption(p: Pick<Playlist, '_id' | 'title'>): PlaylistSelectOption {
  return { _id: p._id, title: p.title };
}

function mergePlaylistOptions(
  list: PlaylistSelectOption[] | undefined,
  extras: PlaylistSelectOption[],
): PlaylistSelectOption[] {
  const byId = new Map<string, PlaylistSelectOption>();
  (list ?? []).forEach((p) => {
    byId.set(p._id, p);
  });
  extras.forEach((p) => {
    if (!byId.has(p._id)) {
      byId.set(p._id, p);
    }
  });
  return [...byId.values()];
}

const EMPTY_SEARCH = (
  <p className="preflight preflight-p my-2">
    No playlists found. Try to{' '}
    <a
      href="/admin/playlists"
      target="_blank"
      rel="noreferrer"
      className="preflight preflight-a font-medium text-primary-green hover:text-secondary-green"
    >
      create a new playlist first
    </a>
    .
  </p>
);

const theme: ThemeType = {
  ...grommetTheme,
  select: {},
};

type SinglePlaylistSelectorProps = Omit<
  SelectExtendedProps,
  'value' | 'onChange' | 'options' | 'onSearch' | 'onOpen' | 'onClose' | 'onMore'
> & {
  multiple?: false;
  value?: string | null;
  onChange?: (id: string | undefined) => void;
};

type MultiPlaylistSelectorProps = Omit<
  SelectMultipleExtendedProps,
  'value' | 'suggestions' | 'onChange' | 'options' | 'onSearch' | 'onOpen' | 'onClose' | 'onMore'
> & {
  multiple: true;
  value?: string[];
  onChange?: (ids: string[]) => void;
};

export type PlaylistSelectorProps = SinglePlaylistSelectorProps | MultiPlaylistSelectorProps;

function listToSelectOptions(rows: { _id: string; title: string }[] | undefined): PlaylistSelectOption[] | undefined {
  return rows?.map((p) => ({ _id: p._id, title: p.title }));
}

function PlaylistSelectorMultiple({ value, onChange, ...rest }: Omit<MultiPlaylistSelectorProps, 'multiple'>) {
  const [searchText, setSearchText] = useState('');

  const trimmedSearchText = searchText.trim();

  const selectedIds = useMemo(() => [...new Set((value ?? []).filter(Boolean))], [value]);

  const { data, fetchNextPage, hasNextPage } = useInfiniteQuery({
    ...getInfiniteAdminPlaylistsQueryOptions({ title: trimmedSearchText, pageSize: 20 }),
  });

  const listOptions = useMemo(() => listToSelectOptions(data?.list), [data?.list]);

  const detailQueries = useQueries({
    queries: selectedIds.map((playlistId) => ({
      ...getPlaylistQueryOption(playlistId),
      enabled: !data?.list.some((p) => p._id === playlistId) && Boolean(playlistId),
    })),
  });

  const extrasFromDetails = useMemo(() => {
    return selectedIds.map((id, i) => {
      const q = detailQueries[i];
      if (q?.data) return playlistToSelectOption(q.data);
      return optionPlaceholder(id);
    });
  }, [selectedIds, detailQueries]);

  const mergedOptions = useMemo(
    () => mergePlaylistOptions(listOptions, extrasFromDetails),
    [listOptions, extrasFromDetails],
  );

  const onSearch = useMemo(() => debounce(setSearchText, 500, { trailing: true }), []);

  return (
    <SelectMultiple
      multiple
      showSelectedInline
      emptySearchMessage={EMPTY_SEARCH}
      value={value}
      labelKey="title"
      valueKey={{ key: '_id', reduce: true }}
      onMore={hasNextPage ? fetchNextPage : undefined}
      onSearch={onSearch}
      options={mergedOptions}
      dropHeight="large"
      onChange={({ value: next }) => {
        const rows = (next ?? []) as string[];
        onChange?.(rows);
      }}
      onClose={() => {
        setSearchText('');
      }}
      {...rest}
    />
  );
}

function PlaylistSelectorSingle({ value, onChange, ...rest }: Omit<SinglePlaylistSelectorProps, 'multiple'>) {
  const [searchText, setSearchText] = useState('');

  const trimmedSearchText = searchText.trim();
  const id = value || undefined;

  const { data, fetchNextPage, hasNextPage } = useInfiniteQuery({
    ...getInfiniteAdminPlaylistsQueryOptions({ title: trimmedSearchText, pageSize: 20 }),
  });

  const listOptions = useMemo(() => listToSelectOptions(data?.list), [data?.list]);

  const { data: detailPlaylist } = useQuery({
    ...getPlaylistQueryOption(id),
    enabled: !data?.list.some((p) => p._id === id) && Boolean(id),
  });

  const detailItem = useMemo((): PlaylistSelectOption | undefined => {
    if (!id) return undefined;
    if (detailPlaylist) return playlistToSelectOption(detailPlaylist);
    return optionPlaceholder(id);
  }, [id, detailPlaylist]);

  const mergedOptions = useMemo(
    () => mergePlaylistOptions(listOptions, detailItem ? [detailItem] : []),
    [listOptions, detailItem],
  );

  const onSearch = useMemo(() => debounce(setSearchText, 500, { trailing: true }), []);

  return (
    <Select
      clear={{ position: 'bottom', label: 'Clear' }}
      emptySearchMessage={EMPTY_SEARCH}
      labelKey="title"
      valueKey={{ key: '_id', reduce: true }}
      options={mergedOptions}
      value={value ?? undefined}
      onChange={({ value: selected }) => {
        onChange?.(!selected ? undefined : (selected as string));
      }}
      onMore={hasNextPage ? fetchNextPage : undefined}
      onSearch={onSearch}
      onClose={() => {
        setSearchText('');
      }}
      {...rest}
    />
  );
}

function PlaylistSelector({ multiple, ...rest }: PlaylistSelectorProps) {
  return (
    <Grommet theme={theme} className="[&>button]:w-full">
      {multiple === true ? (
        <PlaylistSelectorMultiple {...(rest as MultiPlaylistSelectorProps)} />
      ) : (
        <PlaylistSelectorSingle {...(rest as SinglePlaylistSelectorProps)} />
      )}
    </Grommet>
  );
}

export default PlaylistSelector;
